import { useEffect, useState, useRef } from 'react';
import { signInWithCustomToken } from 'firebase/auth';
import { toast } from 'react-toastify';
import { auth } from '../../firebase/config';

/**
 * EmbeddedAuthHandler - Handles token-based auth and platform events for embedded iframe scenarios
 *
 * When Booker is embedded in FutureSense Central Hub or another app, this component:
 * 1. Detects embedded mode via URL param or being in an iframe
 * 2. Listens for FIREBASE_AUTH_TOKEN postMessage from parent
 * 3. Exchanges the token via Central API for a custom token
 * 4. Signs in with the custom token
 * 5. Listens for PLATFORM_EVENT messages (subscription changes, payments, credits)
 */
const EmbeddedAuthHandler = ({ children }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const hasAuthenticated = useRef(false);

  // Platform event listener - always active when embedded
  useEffect(() => {
    const isEmbedded = window.self !== window.top ||
      new URLSearchParams(window.location.search).get('embedded') === 'true';

    if (!isEmbedded) return;

    const handlePlatformEvent = (event) => {
      if (event.data?.type !== 'PLATFORM_EVENT') return;

      const allowedOrigins = [
        'https://futuresenseai.web.app',
        'http://localhost:3000',
        'http://localhost:5173'
      ];
      if (!allowedOrigins.includes(event.origin) && !event.origin.includes('localhost')) return;

      const platformEvent = event.data.event;
      if (!platformEvent?.eventType) return;

      console.log('[PlatformEvent]', platformEvent.eventType, platformEvent);

      try {
        switch (platformEvent.eventType) {
          case 'subscription.created':
          case 'subscription.updated':
            window.dispatchEvent(new CustomEvent('platform:subscription-changed', { detail: platformEvent }));
            break;
          case 'payment.succeeded':
            window.dispatchEvent(new CustomEvent('platform:subscription-changed', { detail: platformEvent }));
            toast.success('Payment successful!', { autoClose: 3000 });
            break;
          case 'credits.purchased':
            window.dispatchEvent(new CustomEvent('platform:credits-changed', { detail: platformEvent }));
            toast.info('Credits added to your account', { autoClose: 3000 });
            break;
          case 'credits.depleted':
            window.dispatchEvent(new CustomEvent('platform:credits-changed', { detail: platformEvent }));
            toast.warn('Your credits have been used up', { autoClose: 5000 });
            break;
          // Cross-app event bus events (silent sync — no toasts)
          case 'invoice.paid':
          case 'invoice.created':
          case 'invoice.overdue':
            window.dispatchEvent(new CustomEvent('platform:invoice-paid', { detail: platformEvent }));
            break;
          case 'payment.received':
          case 'payment.refunded':
            window.dispatchEvent(new CustomEvent('platform:payment-update', { detail: platformEvent }));
            break;
          default:
            // Forward unknown event types as generic platform events
            if (platformEvent.eventType) {
              window.dispatchEvent(new CustomEvent(`platform:${platformEvent.eventType}`, { detail: platformEvent }));
            }
            break;
        }
      } catch (err) {
        console.error('[PlatformEvent] Error handling event:', err);
      }
    };

    window.addEventListener('message', handlePlatformEvent);
    return () => window.removeEventListener('message', handlePlatformEvent);
  }, []);

  useEffect(() => {
    // Check if embedded
    const urlParams = new URLSearchParams(window.location.search);
    const isEmbedded = urlParams.get('embedded') === 'true' || window.self !== window.top;

    if (!isEmbedded) return;

    // If already authenticated, don't process again
    if (auth.currentUser) {
      console.log('Booker: Already authenticated as', auth.currentUser.email);
      hasAuthenticated.current = true;
      return;
    }

    const handleMessage = async (event) => {
      // Verify origin is from allowed parent apps
      const allowedOrigins = [
        'https://futuresenseai.web.app',  // FutureSense Central Hub
        'http://localhost:3000',  // Keep for local development
        'http://localhost:5173'   // Keep for local development
      ];

      const isAllowedOrigin = allowedOrigins.includes(event.origin) ||
        event.origin.includes('localhost');

      if (!isAllowedOrigin) {
        return;
      }

      if (event.data?.type === 'FIREBASE_AUTH_TOKEN' && event.data?.token && !isProcessing && !hasAuthenticated.current) {
        setIsProcessing(true);
        console.log('Booker: Received auth token from parent');

        try {
          // Exchange ID token for custom token via Central API
          // Try Cloud Run URL first (better CORS), fallback to cloudfunctions
          const apiUrls = [
            'https://api-oefjwqkrvq-uc.a.run.app/auth/exchange-token',
            'https://us-central1-paymentgateway-ab783.cloudfunctions.net/api/auth/exchange-token'
          ];

          let response = null;
          let lastError = null;

          for (const url of apiUrls) {
            try {
              console.log('Booker: Trying token exchange at', url);
              response = await fetch(url, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${event.data.token}`
                },
                mode: 'cors'
              });
              if (response.ok) break;
            } catch (e) {
              lastError = e;
              console.warn('Booker: Failed with', url, e.message);
            }
          }

          if (response?.ok) {
            const { customToken } = await response.json();
            console.log('Booker: Got custom token, signing in...');

            // Retry signInWithCustomToken up to 3 times
            let signInSuccess = false;
            for (let attempt = 1; attempt <= 3; attempt++) {
              try {
                await signInWithCustomToken(auth, customToken);
                signInSuccess = true;
                hasAuthenticated.current = true;
                console.log('Booker: Successfully authenticated via embedded token');
                break;
              } catch (signInError) {
                console.warn(`Booker: signInWithCustomToken attempt ${attempt} failed:`, signInError.code, signInError.message);
                if (attempt < 3) {
                  // Wait before retrying (exponential backoff)
                  await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
                }
              }
            }

            if (!signInSuccess) {
              console.error('Booker: All signInWithCustomToken attempts failed');
            }
          } else {
            const errorText = response ? await response.text() : lastError?.message;
            console.error('Booker: Token exchange failed', errorText);
          }
        } catch (error) {
          console.error('Booker: Embedded auth error', error);
        }

        setIsProcessing(false);
      }
    };

    window.addEventListener('message', handleMessage);

    // Signal to parent that we're ready to receive the token
    // NOTE: After architecture flip, Booker is rarely embedded, but keeping for flexibility
    if (window.parent !== window) {
      const parentOrigin = document.referrer ? new URL(document.referrer).origin : 'https://connect.futuresenseai.com';
      window.parent.postMessage({ type: 'BOOKER_READY' }, parentOrigin);
      console.log('Booker: Sent BOOKER_READY to parent (embedded mode active)');
    }

    return () => window.removeEventListener('message', handleMessage);
  }, [isProcessing]);

  return children;
};

export default EmbeddedAuthHandler;
